import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { apiGet, apiPost } from "@/lib/api";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import {
  TrendingUp, Bell, X, CheckCircle2, Clock, Megaphone, BarChart3, RefreshCw,
  Library as LibraryIcon, Search, Users, Sparkles, Layers,
} from "lucide-react";
import { voiceColor } from "@/lib/voiceColors";
import {
  ViewSwitcher, GalleryView, TableView, TimelineView, LibraryDrawer,
} from "@/pages/portal/ContentViews";

const SEEN_KEY = "ll_portal_seen_at";

// ---------------------------------------------------------------------------
// Data hook
// ---------------------------------------------------------------------------
function usePerfData(mode, token, workspaceId) {
  const [perf, setPerf] = useState(null);
  const base = process.env.REACT_APP_BACKEND_URL;
  const reload = useCallback(async () => {
    try {
      if (mode === "public" && token) {
        const r = await axios.get(`${base}/api/p/${token}/performance`);
        setPerf(r.data);
      } else if (workspaceId) {
        setPerf(await apiGet(`/workspaces/${workspaceId}/performance`));
      }
    } catch {
      setPerf(null);
    }
  }, [mode, token, workspaceId, base]);
  useEffect(() => { reload(); }, [reload]);
  return [perf, reload];
}

// ---------------------------------------------------------------------------
// Main tabbed shell (no Library tab — that lives on the standalone Bank screen)
// ---------------------------------------------------------------------------
export function PortalPerformance({ mode, token, workspaceId }) {
  const [perf, reload] = usePerfData(mode, token, workspaceId);
  const [checking, setChecking] = useState(false);
  const [tab, setTab] = useState("overview");

  const checkNow = async () => {
    setChecking(true);
    try {
      await apiPost(`/workspaces/${workspaceId}/performance/run-now`, {});
      toast.success("Checking engagement now — fresh numbers in about a minute");
      let ticks = 0;
      const t = setInterval(async () => {
        ticks += 1;
        await reload();
        if (ticks >= 12) {
          clearInterval(t);
          setChecking(false);
        }
      }, 10000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not start the check");
      setChecking(false);
    }
  };

  const posts = (perf?.posts || []).filter((p) => p.snapshots?.length);
  const followers = perf?.followers || [];
  const totals = useMemo(() => aggregateTotals(posts, followers), [posts, followers]);

  const TABS = [
    { key: "overview",  label: "Overview",  Icon: BarChart3 },
    { key: "posts",     label: "Posts",     Icon: Layers },
    { key: "followers", label: "Followers", Icon: Users },
  ];

  return (
    <section data-testid="portal-performance">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex gap-1" data-testid="performance-tabs">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`performance-tab-${key}`}
              className={`px-4 py-2.5 text-[13px] inline-flex items-center gap-1.5 border-b-2 transition-colors ${
                tab === key
                  ? "border-portal-accent text-portal-accent"
                  : "border-transparent text-stone-600 hover:text-stone-900"
              }`}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={2} /> {label}
            </button>
          ))}
        </div>
        {mode !== "public" && (
          <button
            onClick={checkNow}
            disabled={checking}
            className="portal-btn portal-btn-primary inline-flex items-center gap-1.5"
            data-testid="performance-check-now-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${checking ? "animate-spin" : ""}`} />
            {checking ? "Checking…" : "Check now"}
          </button>
        )}
      </div>

      {tab === "overview" && (
        <OverviewTab totals={totals} followers={followers} posts={posts} />
      )}
      {tab === "posts" && <PostsTab posts={posts} />}
      {tab === "followers" && <FollowersTab followers={followers} />}
    </section>
  );
}

// Standalone Bank screen (Content Library) — kept in this file so it shares
// hooks, filter state persistence, and the shared views.
export function PortalBank({ mode, token, workspaceId }) {
  return <LibraryTab mode={mode} token={token} workspaceId={workspaceId} />;
}

// ---------------------------------------------------------------------------
// Overview — KPI cards + follower mini-chart + top posts
// ---------------------------------------------------------------------------
function aggregateTotals(posts, followers) {
  let totalReactions = 0, totalComments = 0, totalReposts = 0;
  let posts30 = 0;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 30);
  for (const p of posts) {
    const latest = [...(p.snapshots || [])]
      .filter((s) => s.status === "complete")
      .sort((a, b) => (b.taken_at || "").localeCompare(a.taken_at || ""))[0];
    if (latest) {
      totalReactions += latest.likes || 0;
      totalComments += latest.comments || 0;
      totalReposts += latest.reposts || 0;
    }
    if (p.posted_at && new Date(p.posted_at) > cutoff) posts30 += 1;
  }
  const followerDelta = followers.reduce((sum, f) => sum + (f.delta_30d || 0), 0);
  return {
    totalReactions, totalComments, totalReposts,
    posts30, followerDelta,
    postsCount: posts.length,
  };
}

function OverviewTab({ totals, followers, posts }) {
  return (
    <div className="space-y-6" data-testid="performance-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 portal-card divide-x divide-portal-line">
        <KpiCard label="Reactions (tracked)" value={fmtNum(totals.totalReactions)} testid="kpi-reactions" />
        <KpiCard label="Comments (tracked)" value={fmtNum(totals.totalComments)} testid="kpi-comments" />
        <KpiCard label="Reposts (tracked)" value={fmtNum(totals.totalReposts)} testid="kpi-reposts" />
        <KpiCard
          label="Followers (30d)"
          value={totals.followerDelta === 0 ? "0" : (totals.followerDelta > 0 ? `+${fmtNum(totals.followerDelta)}` : fmtNum(totals.followerDelta))}
          tone={totals.followerDelta >= 0 ? "up" : "down"}
          testid="kpi-followers"
        />
      </div>

      {followers.length > 0 && <FollowerChart followers={followers} compact />}

      {posts.length > 0 && (
        <div className="portal-card overflow-hidden">
          <div className="px-6 pt-5 pb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-portal-accent" strokeWidth={2} />
            <span className="text-sm font-bold text-stone-900">Top-performing posts</span>
          </div>
          <PostsTableInner posts={rankByEngagement(posts).slice(0, 5)} compact />
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, tone, testid }) {
  const color = tone === "up" ? "#0E7A56" : tone === "down" ? "#B91C1C" : undefined;
  return (
    <div className="px-5 py-5" data-testid={testid}>
      <div className="portal-overline">{label}</div>
      <div className="portal-serif text-4xl mt-2 font-medium" style={{ color: color || "#1B1E28" }}>
        {value}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Posts tab — sortable table of tracked posts
// ---------------------------------------------------------------------------
function rankByEngagement(posts) {
  const scored = posts.map((p) => {
    const latest = [...(p.snapshots || [])]
      .filter((s) => s.status === "complete")
      .sort((a, b) => (b.taken_at || "").localeCompare(a.taken_at || ""))[0] || {};
    const first = [...(p.snapshots || [])]
      .filter((s) => s.status === "complete")
      .sort((a, b) => (a.taken_at || "").localeCompare(b.taken_at || ""))[0] || {};
    const score = (latest.likes || 0) + (latest.comments || 0) * 2 + (latest.reposts || 0) * 3;
    return { post: p, latest, first, score };
  });
  return scored.sort((a, b) => b.score - a.score).map((r) => ({ ...r.post, __latest: r.latest, __first: r.first }));
}

function PostsTab({ posts }) {
  const [sort, setSort] = useState("engagement");
  const sorted = useMemo(() => {
    if (sort === "engagement") return rankByEngagement(posts);
    // newest
    return [...posts].sort((a, b) => (b.posted_at || "").localeCompare(a.posted_at || ""));
  }, [posts, sort]);

  return (
    <div className="portal-card overflow-hidden" data-testid="performance-posts-tab">
      <div className="px-6 pt-5 pb-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-portal-accent" strokeWidth={2} />
          <span className="text-sm font-bold text-stone-900">Post engagement</span>
        </div>
        <div className="flex items-center gap-1">
          <SortBtn active={sort === "engagement"} onClick={() => setSort("engagement")} testid="posts-sort-engagement">
            Top engagement
          </SortBtn>
          <SortBtn active={sort === "newest"} onClick={() => setSort("newest")} testid="posts-sort-newest">
            Newest
          </SortBtn>
        </div>
      </div>
      {sorted.length === 0 ? (
        <EmptyState msg="No tracked posts yet. Fresh posts get tracked automatically after publish." />
      ) : (
        <PostsTableInner posts={sorted} />
      )}
      <div className="px-6 py-3 text-[11px] text-stone-500 border-t border-portal-line">
        Snapshots are taken about a day after each post goes live, then at 1 week and 1 month, plus on-demand checks. &ldquo;Growth&rdquo; is the change since the first snapshot.
      </div>
    </div>
  );
}

function SortBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-[11px] mono uppercase tracking-wider px-2.5 py-1 rounded transition-colors ${
        active ? "bg-portal-accent/10 text-portal-accent" : "text-stone-500 hover:text-stone-900"
      }`}
    >
      {children}
    </button>
  );
}

function PostsTableInner({ posts, compact = false }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="posts-table">
        <thead>
          <tr className="text-left text-[10px] mono uppercase tracking-wider text-stone-500 border-t border-portal-line">
            <th className="px-6 py-3 font-medium">Post</th>
            <th className="px-3 py-3 font-medium text-right">Reactions</th>
            <th className="px-3 py-3 font-medium text-right">Comments</th>
            <th className="px-3 py-3 font-medium text-right">Reposts</th>
            <th className="px-3 py-3 font-medium text-right">Growth</th>
            {!compact && <th className="px-6 py-3 font-medium text-right">Checked</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-portal-line">
          {posts.map((p) => {
            const latest = p.__latest || [...(p.snapshots || [])]
              .filter((s) => s.status === "complete")
              .sort((a, b) => (b.taken_at || "").localeCompare(a.taken_at || ""))[0] || {};
            const first = p.__first || [...(p.snapshots || [])]
              .filter((s) => s.status === "complete")
              .sort((a, b) => (a.taken_at || "").localeCompare(b.taken_at || ""))[0] || {};
            const growth = (latest.likes || 0) - (first.likes || 0)
                         + (latest.comments || 0) - (first.comments || 0)
                         + (latest.reposts || 0) - (first.reposts || 0);
            const vc = voiceColor(p.voice_profile_id);
            return (
              <tr key={p.my_post_id} className="hover:bg-portal-cream/40 transition-colors" data-testid={`posts-row-${p.my_post_id}`}>
                <td className="px-6 py-4 max-w-[520px]">
                  <div className="text-[13.5px] text-stone-900 leading-snug line-clamp-2">
                    {p.preview || "Untitled post"}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span
                      className="text-[10px] mono uppercase tracking-wider px-1.5 py-0.5 rounded"
                      style={{ background: `${vc.swatch}1a`, color: vc.light }}
                    >
                      {p.voice_profile_name || "voice"}
                    </span>
                    <span className="mono text-[10px] text-stone-500">
                      {(p.posted_at || "").slice(0, 10)}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-4 text-right mono tabular-nums text-stone-900">{fmtNum(latest.likes)}</td>
                <td className="px-3 py-4 text-right mono tabular-nums text-stone-900">{fmtNum(latest.comments)}</td>
                <td className="px-3 py-4 text-right mono tabular-nums text-stone-900">{fmtNum(latest.reposts)}</td>
                <td className="px-3 py-4 text-right mono tabular-nums" style={{ color: growth > 0 ? "#0E7A56" : "#8A8881" }}>
                  {growth > 0 ? "+" : ""}{fmtNum(growth)}
                </td>
                {!compact && (
                  <td className="px-6 py-4 text-right mono text-[11px] text-stone-500">
                    {(latest.taken_at || "").slice(5, 10) || "—"}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Followers tab
// ---------------------------------------------------------------------------
function FollowersTab({ followers }) {
  if (followers.length === 0) {
    return <EmptyState msg="Follower tracking hasn't captured a snapshot yet." />;
  }
  return (
    <div className="space-y-4" data-testid="performance-followers-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {followers.map((vp) => (
          <div key={vp.voice_profile_id} className="portal-card p-6" data-testid={`follower-card-${vp.voice_profile_id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ background: voiceColor(vp.voice_profile_id).swatch }}
                />
                <span className="text-sm font-bold text-stone-900">{vp.voice_profile_name}</span>
              </div>
            </div>
            <div className="portal-serif text-4xl text-stone-900 font-medium tabular-nums">
              {vp.current?.toLocaleString() ?? "—"}
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs">
              <DeltaTag label="7d" delta={vp.delta_7d} />
              <DeltaTag label="30d" delta={vp.delta_30d} />
            </div>
          </div>
        ))}
      </div>
      <FollowerChart followers={followers} />
    </div>
  );
}

function DeltaTag({ label, delta }) {
  if (delta == null) return <span className="mono text-stone-400">{label}: —</span>;
  const color = delta > 0 ? "#0E7A56" : delta < 0 ? "#B91C1C" : "#8A8881";
  return (
    <span className="mono">
      <span className="text-stone-500">{label}:</span>{" "}
      <span style={{ color }} className="font-bold tabular-nums">
        {delta > 0 ? "+" : ""}{delta.toLocaleString()}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
function FollowerChart({ followers, compact = false }) {
  const byDate = {};
  for (const vp of followers) {
    for (const p of vp.series || []) {
      byDate[p.date] = byDate[p.date] || { date: p.date };
      byDate[p.date][vp.voice_profile_name || "Profile"] = p.count;
    }
  }
  const data = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  const singlePoint = data.length < 2;

  return (
    <div className="portal-card p-6" data-testid="follower-chart-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-portal-accent" strokeWidth={2} />
          <span className="text-sm font-bold text-stone-900">Follower growth</span>
        </div>
      </div>
      {singlePoint ? (
        <p className="text-xs text-stone-500">
          First snapshot recorded — the trend line appears after a few days of daily tracking.
        </p>
      ) : (
        <div style={{ width: "100%", height: compact ? 200 : 260 }}>
          <ResponsiveContainer>
            <LineChart data={data} margin={{ top: 6, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#E4E5EC" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9A9DA8" }} tickLine={false} axisLine={{ stroke: "#E4E5EC" }} />
              <YAxis tick={{ fontSize: 11, fill: "#9A9DA8" }} tickLine={false} axisLine={false} width={46} domain={["auto", "auto"]} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E4E5EC", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {followers.map((vp) => (
                <Line
                  key={vp.voice_profile_id}
                  type="monotone"
                  dataKey={vp.voice_profile_name || "Profile"}
                  stroke={voiceColor(vp.voice_profile_id).swatch}
                  strokeWidth={2.5}
                  dot={{ r: 2.5 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LIBRARY tab — Notion-style content database with multiple views
// ---------------------------------------------------------------------------
const LIBRARY_VIEW_KEY = "ll_portal_library_view";

function LibraryTab({ mode, token, workspaceId }) {
  const base = process.env.REACT_APP_BACKEND_URL;
  const [q, setQ] = useState("");
  const [vp, setVp] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem(LIBRARY_VIEW_KEY) || "gallery");
  const [drawerItem, setDrawerItem] = useState(null);
  const pageSize = view === "table" ? 30 : 24;

  useEffect(() => { localStorage.setItem(LIBRARY_VIEW_KEY, view); }, [view]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q: q || undefined, voice_profile_id: vp || undefined,
        from: dateFrom || undefined, to: dateTo || undefined,
        sort, page, page_size: pageSize };
      let r;
      if (mode === "public" && token) {
        const resp = await axios.get(`${base}/api/p/${token}/content-library`, { params });
        r = resp.data;
      } else if (workspaceId) {
        r = await apiGet(`/workspaces/${workspaceId}/content-library`, params);
      }
      setData(r);
    } catch {
      setData({ items: [], total: 0, voice_profiles: [] });
    } finally {
      setLoading(false);
    }
  }, [mode, token, workspaceId, base, q, vp, dateFrom, dateTo, sort, page, pageSize]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [q, vp, dateFrom, dateTo, sort]);

  const items = useMemo(() => (data?.items || []).map(toPortalItem), [data]);
  const total = data?.total || 0;
  const voiceProfiles = data?.voice_profiles || [];
  const hasFilters = q || vp || dateFrom || dateTo;

  return (
    <div className="space-y-4" data-testid="performance-library-tab">
      {/* Filter + view switcher bar */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="library-toolbar">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            type="text"
            placeholder="Search posts…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="portal-input pl-9"
            data-testid="library-search"
          />
        </div>
        {voiceProfiles.length > 0 && (
          <select
            value={vp}
            onChange={(e) => setVp(e.target.value)}
            className="portal-input max-w-[180px]"
            data-testid="library-voice-filter"
          >
            <option value="">All voices</option>
            {voiceProfiles.map((v) => (
              <option key={v.voice_profile_id} value={v.voice_profile_id}>
                {v.voice_profile_name} ({v.count})
              </option>
            ))}
          </select>
        )}
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="portal-input max-w-[150px]"
          data-testid="library-date-from"
          aria-label="From date"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="portal-input max-w-[150px]"
          data-testid="library-date-to"
          aria-label="To date"
        />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="portal-input max-w-[170px]"
          data-testid="library-sort"
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="top_engagement">Top engagement</option>
        </select>
        {hasFilters && (
          <button
            onClick={() => { setQ(""); setVp(""); setDateFrom(""); setDateTo(""); }}
            className="portal-btn portal-btn-ghost inline-flex items-center gap-1"
            data-testid="library-clear"
          >
            <X className="w-3 h-3" /> clear
          </button>
        )}
        <div className="ml-auto">
          <ViewSwitcher
            view={view}
            onChange={setView}
            views={["gallery", "table", "timeline"]}
            testid="library-view-switcher"
          />
        </div>
      </div>

      {/* Summary line */}
      <div className="flex items-center justify-between text-xs text-stone-500 px-1">
        <span data-testid="library-count">
          {loading ? "loading…" : `${total.toLocaleString()} published post${total === 1 ? "" : "s"}`}
          {hasFilters && !loading && " matching your filters"}
        </span>
      </div>

      {/* Views */}
      {loading && items.length === 0 ? (
        <div className="portal-card p-10 text-center text-sm text-stone-500">Loading library…</div>
      ) : view === "gallery" ? (
        <GalleryView items={items} onOpen={setDrawerItem} testid="library-gallery" />
      ) : view === "table" ? (
        <TableView items={items} onOpen={setDrawerItem} showMetrics testid="library-table" />
      ) : (
        <TimelineView items={items} onOpen={setDrawerItem} testid="library-timeline" />
      )}

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-between text-xs text-stone-500" data-testid="library-pagination">
          <span>
            page {page} of {Math.max(1, Math.ceil(total / pageSize))}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="portal-btn portal-btn-ghost"
              data-testid="library-prev"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!data?.has_more}
              className="portal-btn portal-btn-ghost"
              data-testid="library-next"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {drawerItem && (
        <LibraryDrawer item={drawerItem} onClose={() => setDrawerItem(null)} />
      )}
    </div>
  );
}

// Map API row → shared PortalItem shape.
function toPortalItem(row) {
  const full = row.post_text || "";
  return {
    id: row.my_post_id,
    preview: full.slice(0, 260),
    full_text: full,
    date: row.post_date,
    voice_id: row.voice_profile_id,
    voice_name: row.voice_profile_name,
    external_url: row.post_url,
    metrics: {
      likes: row.engagement?.likes || 0,
      comments: row.engagement?.comments || 0,
      reposts: row.engagement?.reposts || 0,
    },
  };
}

// ---------------------------------------------------------------------------
function EmptyState({ msg }) {
  return (
    <div className="portal-card px-10 py-12 text-center text-sm text-stone-500" data-testid="portal-empty">
      {msg}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PortalUpdates — right-side slide-in panel (fixed the "middle-of-screen" bug)
// ---------------------------------------------------------------------------
const EVENT_ICON = {
  review_ready: CheckCircle2,
  review_reminder: Clock,
  published: Megaphone,
  weekly_digest: TrendingUp,
};

export function PortalUpdates({ mode, token, workspaceId }) {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [seenAt, setSeenAt] = useState(() => localStorage.getItem(SEEN_KEY) || "");
  const wrapRef = useRef(null);
  const base = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    (async () => {
      try {
        let evs = [];
        if (mode === "public" && token) {
          const r = await axios.get(`${base}/api/p/${token}/events`);
          evs = r.data?.events || [];
        } else if (workspaceId) {
          const r = await apiGet(`/workspaces/${workspaceId}/portal-events`);
          evs = r?.events || [];
        }
        setEvents(evs);
        // Never auto-open. The bell badge signals unread count; the client
        // opens the panel by clicking the bell.
      } catch {
        setEvents([]);
      }
    })();
  }, [mode, token, workspaceId, base]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const unseen = events.filter((e) => e.created_at > seenAt).length;

  const markSeen = () => {
    const now = new Date().toISOString();
    localStorage.setItem(SEEN_KEY, now);
    setSeenAt(now);
    setOpen(false);
  };

  if (events.length === 0) return null;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="portal-btn inline-flex items-center gap-2 relative"
        data-testid="portal-updates-bell"
      >
        <Bell className="w-3.5 h-3.5" />
        Updates
        {unseen > 0 && (
          <span
            className="text-[10px] font-bold text-white rounded-full px-1.5 py-0.5"
            style={{ background: "#4F46E5" }}
            data-testid="portal-updates-count"
          >
            {unseen}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 z-[70] w-[min(420px,94vw)] max-h-[520px] portal-light bg-white rounded-2xl overflow-hidden flex flex-col"
          style={{
            boxShadow: "0 20px 60px rgba(17,24,39,0.14), 0 4px 12px rgba(17,24,39,0.08)",
            animation: "portalFadeIn 180ms ease-out",
          }}
          data-testid="portal-updates-panel"
          role="dialog"
          aria-label="Updates"
        >
          <div className="flex items-start justify-between px-5 pt-4 pb-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <img
                src="/owlbert/main.png"
                alt="Owlbert"
                className="w-8 h-8 object-contain shrink-0"
              />
              <div className="min-w-0">
                <div className="portal-overline" style={{ fontSize: 10 }}>While you were away</div>
                <h3 className="portal-serif text-lg text-stone-900 mt-0.5 leading-tight">Updates</h3>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="portal-btn portal-btn-ghost p-1.5 shrink-0"
              data-testid="portal-updates-close"
              aria-label="Close updates"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-3 pb-2 space-y-1.5">
            {events.slice(0, 30).map((e) => {
              const Icon = EVENT_ICON[e.kind] || Bell;
              const isNew = e.created_at > seenAt;
              return (
                <div
                  key={e.id}
                  className={`flex items-start gap-2.5 py-2.5 px-3 rounded-xl ${isNew ? "bg-[#eef2ff]/40" : "hover:bg-[#fafafa]"}`}
                  data-testid={`portal-event-${e.id}`}
                >
                  <Icon className="w-3.5 h-3.5 text-portal-accent mt-0.5 shrink-0" strokeWidth={2} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-stone-900 font-semibold leading-snug break-words">
                      {e.title}
                      {isNew && (
                        <span
                          className="ml-1.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ color: "#4F46E5", background: "rgba(79,70,229,0.1)" }}
                        >
                          new
                        </span>
                      )}
                    </div>
                    {e.body && (
                      <div className="text-[11.5px] text-stone-600 mt-0.5 leading-relaxed break-words">{e.body}</div>
                    )}
                  </div>
                  <div className="mono text-[10px] text-stone-400 shrink-0 pt-0.5">
                    {(e.created_at || "").slice(5, 10)}
                  </div>
                </div>
              );
            })}
          </div>

          {unseen > 0 && (
            <div className="px-3 pb-3 pt-1">
              <button
                onClick={markSeen}
                className="w-full text-[11px] font-semibold text-[#4F46E5] hover:text-[#3730a3] py-2 rounded-lg hover:bg-[#eef2ff]/60 transition-colors"
                data-testid="portal-updates-gotit"
              >
                Mark all as read
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function fmtNum(n) {
  if (n === null || n === undefined) return 0;
  if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
  if (n >= 10000) return (n / 1000).toFixed(1) + "k";
  return n;
}
